/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: auto-generated patient/provider wording.
// Source: cardiac_report_v0_5_6_dev_2.html lines 4457-4673 (auto-note suite),
// 5285-5311 (patientFollowUpText), 5486-5521 (providerSummaryText,
// providerRecommendationsText), 3182-3195 (sanitizeProviderFollowUpText),
// 6706-6792 (generateInterpretation, generateProviderInterpretation).
// Deviations: currentHeartRateBpm()/data globals -> explicit hrBpm/features args.
// Heart Report v0.5.8 at cardiac-panel-v4 git 94432d0: a card with no result
// (unassessed, or left out for want of a measurement) carries wording that
// says so, and the patient follow-up text is decided by the cards in the
// report only (a card without an inclusion state, from a document saved
// before cards carried one, counts as in the report). Deviation: the provider
// summary follows the same rule — upstream still reads every card, so an
// unassessed (grey) Structural Sound card words its summary as a consistent
// additional sound pattern; here a domain with no card in the report is
// stated as not assessed, and that sentence is regenerated from the cards
// each time rather than kept as imported.
// Medaica Report v0.6.1 (cardiac-panel-v4 git 6f8684d): the Cycle Regularity
// provider note names the pattern its resolved status stands for — the card
// quotes a timing deviation (small = steady), which must never be re-read as
// the legacy percent-regular figure (large = steady) — and the patient
// follow-up and provider summary grade each card the way the cards show it
// (`effectiveFeatureColorClass`: a reviewer's marker is authoritative), not
// from the imported fields alone.

import {
  isCardiacCycleTimingFeature,
  isSysDiaDurationFeature,
  isRhythmRegularityFeature,
  findFeatureByName,
} from "./features"
import { cardiacCycleRatioZonesForHeartRate, expectedSysDiaRangesForHeartRate } from "./zones"
import { parseSysDiaDurationObserved } from "./parse"
import {
  rhythmRegularityPatternLabel,
  rhythmRegularityProviderMethodText,
  rhythmRegularityProviderMethodFromFeature,
  pcgTimingDisplayLabel,
  timingPatternLabelForStatus,
} from "./timing-pattern"
import { getStatusLabel, structuralSoundResultLabel, effectiveFeatureColorClass } from "./status"
import {
  INCLUSION_ACCEPTED,
  INCLUSION_UNRESOLVED,
  MEASUREMENT_UNAVAILABLE_PATIENT_TEXT,
  UNRESOLVED_PATIENT_TEXT,
} from "./inclusion"

// How the provider summary words a domain none of whose cards is in the report
const NOT_IN_REPORT_MARKER = "not assessed in this report"

// The provider summary's sentence for a Structural Sound card in the report
const STRUCTURAL_SUMMARY_SENTENCES = {
  green: "No consistently recurring additional sound pattern was identified between or around the usual heart sounds.",
  amber:
    "An additional sound pattern was identified between or around the usual heart sounds across multiple heartbeats.",
  red: "A consistent additional sound pattern was identified between or around the usual heart sounds across most reviewed heartbeats.",
}

const inReport = (feature) => !feature.reportInclusionStatus || feature.reportInclusionStatus === INCLUSION_ACCEPTED

export function getPatientExplanationText(featureName) {
  const n = featureName.toLowerCase()
  if (n.includes("signal") || n.includes("quality")) {
    return "This checks whether the signal was clear enough to support the screening result."
  }
  if (n.includes("heart rate") || n.includes("rate")) {
    return "Heart rate is the number of heartbeats per minute. Resting heart rate varies with age, fitness, pregnancy, medications, stress, and recent activity."
  }
  if (n.includes("timing summary")) {
    return "This summarizes the timing parts of the screening, including heart rate, cardiac cycle timing, and rhythm regularity."
  }
  if (isSysDiaDurationFeature(featureName)) {
    return "This checks the measured squeezing and refilling parts of the cardiac cycle. At rest, the refilling window is usually longer than the squeezing window and varies with heart rate."
  }
  if (isCardiacCycleTimingFeature(featureName)) {
    return "This checks the relationship between the squeezing and refilling parts of the cardiac cycle. At rest, the cardiac cycle usually spends more time refilling than squeezing."
  }
  if (n.includes("regularity") || n.includes("rhythm")) {
    return "This estimates how steady the timing was from one cardiac cycle to the next in the heart-sound recording. It is a PCG timing proxy, not an ECG rhythm test."
  }
  if (
    n.includes("cardiac cycle") ||
    n.includes("s/d") ||
    n.includes("sys") ||
    n.includes("interval") ||
    n.includes("timing")
  ) {
    return "A healthy resting cardiac cycle usually spends more time refilling than squeezing. This screening checks whether that timing relationship appeared within the normal resting range."
  }
  if (
    n.includes("structural") ||
    n.includes("sound") ||
    n.includes("systolic") ||
    n.includes("diastolic") ||
    n.includes("acoustic") ||
    n.includes("murmur") ||
    n.includes("activity")
  ) {
    return "This screening looks for additional sound patterns between or around the usual heart sounds that recur across multiple heartbeats. These patterns can have many causes and are not, by themselves, a diagnosis."
  }
  return "This screening reviews one part of the cardiac screening result."
}

export function getPatientResultText(featureName, colorClass) {
  const n = featureName.toLowerCase()
  const status = colorClass // 'green' | 'amber' | 'red'
  if (n.includes("signal") || n.includes("quality")) {
    return {
      green: "Signal quality was sufficient to complete the screening assessment.",
      amber: "Signal quality was sufficient to issue the report, with conservative interpretation where needed.",
      red: "The report should not be issued if recording quality is too low.",
    }[status]
  }
  if (n.includes("heart rate") || n.includes("rate")) {
    return {
      green: "Your result was within the normal resting range.",
      amber:
        "Your result appeared in the borderline range. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
      red: "Your result was outside the normal resting range. Please discuss this report with your healthcare provider.",
    }[status]
  }
  if (n.includes("timing summary")) {
    return {
      green: "Your summary result was within the normal range.",
      amber:
        "One or more timing areas was near or slightly outside the normal range. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
      red: "One or more timing areas was outside the normal range. Please discuss this report with your healthcare provider.",
    }[status]
  }
  if (isSysDiaDurationFeature(featureName)) {
    return {
      green: "Your cardiac cycle duration pattern appeared normal for the observed heart rate.",
      amber:
        "Your cardiac cycle duration pattern was in the borderline range for the observed heart rate. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
      red: "Your cardiac cycle duration pattern was outside the expected range for the observed heart rate. Please discuss this report with your healthcare provider.",
    }[status]
  }
  if (isCardiacCycleTimingFeature(featureName)) {
    return {
      green: "Your SYS:DIA timing relationship appeared normal for a resting recording.",
      amber:
        "Your SYS:DIA timing relationship was in the borderline range for a resting recording. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
      red: "Your SYS:DIA timing relationship was outside the expected resting range. Please discuss this report with your healthcare provider.",
    }[status]
  }
  if (n.includes("regularity") || n.includes("rhythm")) {
    return {
      green: "The timing between heart sounds appeared consistent in this recording.",
      amber: "The timing between heart sounds appeared to show some variation in this recording.",
      red: "The timing between heart sounds appeared to show increased variation in this recording. Your care team will review this.",
    }[status]
  }
  if (
    n.includes("structural") ||
    n.includes("sound") ||
    n.includes("systolic") ||
    n.includes("diastolic") ||
    n.includes("acoustic") ||
    n.includes("murmur") ||
    n.includes("activity")
  ) {
    return {
      green:
        "No consistently recurring additional sound pattern was identified between or around the usual heart sounds.",
      amber:
        "An additional sound pattern was identified between or around the usual heart sounds across multiple heartbeats. Discuss this result with your healthcare professional.",
      red: "A consistent additional sound pattern was identified between or around the usual heart sounds across most reviewed heartbeats. Discuss this result with your healthcare professional.",
    }[status]
  }
  return {
    green: "This part of the screening was within the normal range.",
    amber:
      "This part of the screening was near or slightly outside the normal range. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
    red: "This part of the screening was outside the normal range and should be discussed with your healthcare provider.",
  }[status]
}

export function composePatientText(explanation, resultText) {
  return [explanation, resultText]
    .map((v) => String(v || "").trim())
    .filter(Boolean)
    .join("\n\n")
}

export function composePatientNote(feature) {
  if (!feature) return ""
  return composePatientText(feature.patientExplanationText, feature.patientResultText) || feature.noteToPatient || ""
}

export function getAutoPatientNote(featureName, colorClass) {
  return composePatientText(getPatientExplanationText(featureName), getPatientResultText(featureName, colorClass))
}

export function setAutoPatientCopy(feature, colorClass) {
  if (!feature) return
  feature.patientExplanationText = getPatientExplanationText(feature.name)
  // A card with no result says so; it never takes a result's wording
  if (feature.measurementUnavailable) {
    feature.patientResultText = MEASUREMENT_UNAVAILABLE_PATIENT_TEXT
  } else if (feature.reportInclusionStatus === INCLUSION_UNRESOLVED && !feature.manualAssessmentStarted) {
    feature.patientResultText = UNRESOLVED_PATIENT_TEXT
  } else {
    feature.patientResultText = getPatientResultText(feature.name, colorClass)
  }
  feature.noteToPatient = composePatientNote(feature)
}

// System-authored wording is recognised by content, not only by the edited
// flags, so a saved document whose flags are wrong still regenerates its
// generated text after a result change while genuinely clinician-authored
// wording stays protected.
export function patientCopyLooksAutoGenerated(feature) {
  if (!feature) return false
  const savedResult = String(feature.patientResultText || "").trim()
  const savedNote = String(feature.noteToPatient || "").trim()
  return ["green", "amber", "red"].some((status) => {
    const generated = String(getPatientResultText(feature.name, status) || "").trim()
    return generated && (savedResult === generated || savedNote.includes(generated))
  })
}

export function providerCopyLooksAutoGenerated(feature, hrBpm) {
  if (!feature) return false
  const saved = String(feature.noteToDoctor || "").trim()
  if (!saved) return true
  if (
    isRhythmRegularityFeature(feature) &&
    /^(Normal|Moderate|Elevated|Periodic|N\/A)\.\s+.*PCG timing proxy; not ECG HRV or ECG rhythm diagnosis\.$/i.test(
      saved
    )
  ) {
    return true
  }
  return ["green", "amber", "red"].some((status) => {
    const generated = String(
      getAutoProviderNote(
        feature.name,
        status,
        feature.observed,
        feature.displayReferenceRange || feature.normalRange,
        hrBpm
      ) || ""
    ).trim()
    return generated === saved
  })
}

export function hasKnownStaleAutoPatientCopy(feature, colorClass) {
  if (!feature || colorClass === "green") return false
  const result = String(feature.patientResultText || "").trim()
  const note = String(feature.noteToPatient || "").trim()
  const staleAutoPhrases = [
    "The timing between heart sounds appeared consistent in this recording.",
    "Your cardiac cycle duration pattern was near the normal range for the observed heart rate. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
    "Your SYS:DIA timing relationship was near the normal resting range. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
    "The squeezing and refilling timing pattern appeared near the normal range for the observed heart rate. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
    "Your cardiac cycle timing relationship was near the normal resting range. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
  ]
  return staleAutoPhrases.includes(result) || staleAutoPhrases.some((phrase) => note.includes(phrase))
}

export function patientNoteDisplay(feature, hrBpm) {
  if (!feature) return ""
  const colorClass = effectiveFeatureColorClass(feature, hrBpm)
  if (isRhythmRegularityFeature(feature) && colorClass !== "green") {
    const currentResultText = getPatientResultText(feature.name, colorClass)
    const savedResultText = String(feature.patientResultText || "").trim()
    const savedNote = String(feature.noteToPatient || "").trim()
    const autoNormalText = getPatientResultText(feature.name, "green")
    const autoAmberText = getPatientResultText(feature.name, "amber")
    const autoRedText = getPatientResultText(feature.name, "red")
    const looksAutoGenerated =
      !savedResultText ||
      savedResultText === autoNormalText ||
      savedResultText === autoAmberText ||
      savedResultText === autoRedText ||
      savedNote.includes(autoNormalText) ||
      savedNote.includes(autoAmberText) ||
      savedNote.includes(autoRedText)
    if (looksAutoGenerated) {
      return composePatientText(getPatientExplanationText(feature.name), currentResultText)
    }
  }
  if (hasKnownStaleAutoPatientCopy(feature, colorClass)) {
    return composePatientText(getPatientExplanationText(feature.name), getPatientResultText(feature.name, colorClass))
  }
  return composePatientNote(feature)
}

export function getAutoProviderNote(featureName, colorClass, observed, normalRange, hrBpm) {
  const n = featureName.toLowerCase()
  const s = colorClass
  const referenceText = isCardiacCycleTimingFeature(featureName)
    ? cardiacCycleRatioZonesForHeartRate(hrBpm).label
    : isSysDiaDurationFeature(featureName)
    ? expectedSysDiaRangesForHeartRate(hrBpm).label
    : normalRange
  const val = observed ? `Observed: ${observed}${referenceText ? " (" + referenceText + ")" : ""}.` : ""
  // The timing cards quote the value and the reference as two sentences
  // (Heart Report v0.5.8 at cardiac-panel-v4 git c191420); Heart Rate's
  // reference is in the sentence that follows
  const observedSentence = observed ? `Observed: ${String(observed).trim()}.` : ""
  const referenceSentence = referenceText ? ` ${referenceText}.` : ""

  if (n.includes("signal") || n.includes("quality")) {
    return {
      green: `${val} Good.`,
      amber: `${val} Fair.`,
      red: `${val} Poor signal quality; repeat recording required before report submission.`,
    }[s]
  }
  if (n.includes("heart rate") || n.includes("rate")) {
    return {
      green: `${observedSentence} Resting heart rate within normal adult range (60–100 bpm). PCG-guided estimate only; not an ECG rhythm diagnosis. Ref: AHA/ACC 2019 Guideline on Primary Prevention of Cardiovascular Disease.`,
      amber: `${observedSentence} Resting heart rate near the normal adult range (60–100 bpm). Clinical correlation with history, medications, and symptoms advised. Consider 12-lead ECG if sustained or symptomatic. Ref: AHA/ACC 2019.`,
      red: `${observedSentence} Resting heart rate outside normal adult range (60–100 bpm). Clinical correlation required. Consider 12-lead ECG rhythm evaluation. Ref: AHA/ACC 2019 Guideline on Primary Prevention of Cardiovascular Disease.`,
    }[s]
  }
  if (isSysDiaDurationFeature(featureName)) {
    // Name both phase values; a one-number (legacy) entry is incomplete and
    // must not be described as both durations being within range.
    const durations = parseSysDiaDurationObserved(observed)
    const complete = Number.isFinite(durations.sysMs) && Number.isFinite(durations.diaMs)
    if (!complete) {
      return `Observed duration entry is incomplete. ${
        referenceText ? `${referenceText}. ` : ""
      }Both SYS and DIA durations are required before this card can be interpreted.`
    }
    // The observed text as the card shows it — a range per phase stays a range
    const durationValue = `${observedSentence}${referenceSentence}`
    return {
      green: `${durationValue} Mean systolic and diastolic durations are within expected screening range. PCG timing proxy; not an ECG rhythm diagnosis.`,
      amber: `${durationValue} One or both phase durations are near the expected screening range. Correlate with heart rate, cycle regularity, and clinical context.`,
      red: `${durationValue} One or both phase durations are outside the expected screening range. Correlate with heart rate, cycle regularity, and clinical context.`,
    }[s]
  }
  if (n.includes("regularity") || n.includes("rhythm")) {
    const method = rhythmRegularityProviderMethodText(observed)
    // The resolved card status decides the pattern; the quoted value is not
    // re-read (a timing deviation is not a percent-regular score)
    const displayPattern = pcgTimingDisplayLabel(timingPatternLabelForStatus(s))
    return {
      green: `${displayPattern}. ${method} PCG timing proxy; not ECG HRV or ECG rhythm diagnosis.`,
      amber: `${displayPattern}. ${method} PCG timing proxy; not ECG HRV or ECG rhythm diagnosis.`,
      red: `${displayPattern}. ${method} PCG timing proxy; not ECG HRV or ECG rhythm diagnosis.`,
    }[s]
  }
  if (n.includes("cardiac cycle") || n.includes("s/d") || n.includes("interval") || n.includes("timing")) {
    if (n.includes("summary")) {
      return {
        green: `${val} PCG timing pattern regular. No ECG rhythm diagnosis implied.`,
        amber: `${val} PCG timing pattern variable. Correlate clinically. No ECG rhythm diagnosis implied.`,
        red: `${val} PCG timing pattern irregular. Consider ECG-based rhythm assessment if clinically indicated.`,
      }[s]
    }
    const timingValue = `${observedSentence}${referenceSentence}`
    return {
      green: `${timingValue} PCG-derived SYS:DIA timing ratio is within expected resting screening range. Interpret with heart rate, cycle regularity, signal quality, and clinical context.`,
      amber: `${timingValue} PCG-derived SYS:DIA timing ratio is near expected resting screening range. Correlate clinically.`,
      red: `${timingValue} PCG-derived SYS:DIA timing ratio is outside expected resting screening range. Correlate clinically.`,
    }[s]
  }
  if (
    n.includes("structural") ||
    n.includes("sound") ||
    n.includes("systolic") ||
    n.includes("diastolic") ||
    n.includes("acoustic") ||
    n.includes("murmur") ||
    n.includes("activity")
  ) {
    const timing = n.includes("diastolic") ? "diastolic" : n.includes("systolic") ? "systolic" : ""
    const location = timing ? ` in ${timing} phase` : ""
    // The wording follows the imported result text itself, so a negated
    // result ("No significant additional …", "No prominent …") can never be
    // paired with positive language; no causal mechanism is inferred.
    const normalizedObserved = String(observed || "").toLowerCase()
    const structuralStatus =
      normalizedObserved.includes("no significant additional") || normalizedObserved.includes("no prominent")
        ? "green"
        : normalizedObserved.includes("prominent") || normalizedObserved.includes("elevated")
        ? "red"
        : normalizedObserved.includes("additional heart-sound energy") ||
          normalizedObserved.includes("additional heart sound energy") ||
          normalizedObserved.includes("borderline")
        ? "amber"
        : s
    const structuralLabel = structuralSoundResultLabel({ observed }, structuralStatus)
    if (structuralLabel === "Unable to assess") {
      return `${val} Structural-sound guide evidence was limited, so no reportable repeatable structural-sound pattern should be inferred from this card. Review signal quality, S1/S2 guide confidence, and other exam findings.`
    }
    return {
      green: `${val} No additional repeatable structural-sound pattern was identified${location}. This does not exclude structural pathology.`,
      amber: `${val} An additional sound-energy pattern was identified${location}. Interpret with the recording, examination, history, and symptoms.`,
      red: `${val} A prominent repeatable additional sound pattern was identified${location}. Clinical review is recommended.`,
    }[structuralStatus]
  }
  // Generic fallback
  return {
    green: `${val} Within acceptable screening range.`,
    amber: `${val} Borderline result. Clinical correlation recommended.`,
    red: `${val} Outside acceptable screening range. Clinical correlation required.`,
  }[s]
}

export function sanitizeProviderFollowUpText(text) {
  const note = String(text || "").trim()
  if (!note) return ""
  const lower = note.toLowerCase()
  const stalePcgImportLanguage =
    lower.includes("pcg viewer evidence has been imported as unreviewed support") ||
    lower.includes("raw audio") ||
    lower.includes("waveform before sign-off") ||
    lower.includes("source signal quality")
  if (stalePcgImportLanguage) {
    return "This screening report is intended to support clinician assessment, not replace it. Interpret alongside relevant history, symptoms, physical examination, and standard clinical testing when clinically indicated."
  }
  return note
}

export function patientFollowUpText(candidateText, features, hrBpm) {
  const text = String(candidateText || "").trim()
  const stale =
    !text ||
    /seek urgent care/i.test(text) ||
    /dial 911/i.test(text) ||
    /does not replace medical evaluation/i.test(text) ||
    /for non-urgent concerns/i.test(text)
  if (!stale) return text

  // Only the cards in the report speak for it: a pending, unassessed or
  // excluded card is not a finding
  const structuralCard = findFeatureByName(features, "Structural Sound")
  const structural = structuralCard && inReport(structuralCard) ? structuralCard : null
  // Each card is graded as the report shows it
  const structuralColor = structural ? effectiveFeatureColorClass(structural, hrBpm) : "green"
  if (structuralColor === "red") {
    return "This screening found an elevated heart-sound pattern. Arrange healthcare-professional review."
  }
  if (structuralColor === "amber") {
    return "This screening found a borderline heart-sound pattern. Please discuss this report with your healthcare provider if you have symptoms or concerns."
  }

  const timingCards = ["Heart Rate", "SYS:DIA Ratio", "SYS:DIA Duration", "Cycle Regularity"]
    .map((name) => findFeatureByName(features, name))
    .filter((feature) => feature && inReport(feature))
  const timingIssue = timingCards.some((feature) => effectiveFeatureColorClass(feature, hrBpm) !== "green")
  if (timingIssue) {
    return "One or more timing results was outside the expected range. Please discuss this report with your healthcare provider if you have symptoms or concerns."
  }
  return "This screening did not identify a result outside the expected range. Please discuss this report with your healthcare provider if you have symptoms or concerns."
}

export function providerSummaryText(candidateText, features, hrBpm) {
  const text = String(candidateText || "").trim()
  const stale =
    !text ||
    /draft screening summary/i.test(text) ||
    /reviewing clinician validation/i.test(text) ||
    /required before release/i.test(text) ||
    // A summary that says a domain is not in the report follows the cards:
    // the clinician may since have assessed and saved one by hand
    text.includes(NOT_IN_REPORT_MARKER)
  if (!stale) return text

  // Only the cards in the report speak for it
  const heartRate = findFeatureByName(features, "Heart Rate")
  const cycle = findFeatureByName(features, "SYS:DIA Ratio")
  const rhythm = findFeatureByName(features, "Cycle Regularity")
  const structuralCard = findFeatureByName(features, "Structural Sound")
  const structural = structuralCard && inReport(structuralCard) ? structuralCard : null
  const timingFeatures = [heartRate, cycle, rhythm].filter((feature) => feature && inReport(feature))
  // Each card is graded as the report shows it
  const timingIssue = timingFeatures.some((feature) => effectiveFeatureColorClass(feature, hrBpm) !== "green")
  const structuralColor = structural ? effectiveFeatureColorClass(structural, hrBpm) : "green"
  const foundTimingSentence = timingIssue
    ? "One or more heart-sound timing findings is outside the expected screening range."
    : "Heart-sound timing findings are within expected screening ranges."
  const timingSentence = timingFeatures.length ? foundTimingSentence : `Heart-sound timing is ${NOT_IN_REPORT_MARKER}.`
  const structuralSentence = structural
    ? STRUCTURAL_SUMMARY_SENTENCES[structuralColor] || STRUCTURAL_SUMMARY_SENTENCES.red
    : `Additional heart-sound patterns are ${NOT_IN_REPORT_MARKER}.`
  return `${timingSentence} ${structuralSentence}`
}

export function providerRecommendationsText(candidateText) {
  const text = String(candidateText || "").trim()
  const stale =
    !text ||
    /repeat recording if signal quality/i.test(text) ||
    /placement is questioned/i.test(text) ||
    /recommend clinician review/i.test(text) ||
    /raw audio|waveform/i.test(text)
  if (!stale) return text
  return "Interpret alongside clinical history, physical examination, and standard clinical testing when clinically indicated. PCG screening only; not an ECG or echocardiogram."
}

/**
 * The status label the interpretation templates are keyed by. A heart-rate
 * range that crosses reference categories is labelled "Mixed range" and
 * graded amber as a whole, so its interpretation is the borderline one.
 */
function interpretationStatusLabel(feature, colorClass) {
  const statusText = getStatusLabel(feature, colorClass)
  return statusText === "Mixed range" ? "Borderline" : statusText
}

export function generateInterpretation(feature, colorClass) {
  if (colorClass === "grey") {
    return "Diagnosis not yet available — awaiting clinical assessment"
  }
  if (feature.name === "Structural Sound" || feature.name === "Structural Sound Summary") {
    const structuralLabel = structuralSoundResultLabel(feature, colorClass)
    if (structuralLabel === "Unable to assess") {
      return "This recording did not provide enough reliable structural-sound guide evidence to assess this area. Please discuss this report with your healthcare provider if you have symptoms or concerns."
    }
    if (colorClass === "green")
      return (
        feature.patientNormal ||
        "No consistently recurring additional sound pattern was identified between or around the usual heart sounds."
      )
    if (colorClass === "amber")
      return (
        feature.patientBorderline ||
        "An additional sound pattern was identified between or around the usual heart sounds across multiple heartbeats. Discuss this result with your healthcare professional."
      )
    if (colorClass === "red")
      return (
        feature.patientAbnormal ||
        "A consistent additional sound pattern was identified between or around the usual heart sounds across most reviewed heartbeats. Discuss this result with your healthcare professional."
      )
  }
  if (isRhythmRegularityFeature(feature)) {
    if (colorClass === "green" && feature.patientNormal) return feature.patientNormal
    if (colorClass === "amber" && feature.patientBorderline) return feature.patientBorderline
    if (colorClass === "red" && feature.patientAbnormal) return feature.patientAbnormal
  }
  const statusText = interpretationStatusLabel(feature, colorClass)

  if (statusText === "Normal" && feature.patientNormal) return feature.patientNormal
  if (statusText === "Borderline" && feature.patientBorderline) return feature.patientBorderline
  if ((statusText === "Elevated" || statusText === "Low") && feature.patientAbnormal) return feature.patientAbnormal

  const templates = {
    Normal: "Measurement within normal range.",
    Borderline:
      "Measurement shows variation from typical range; continue monitoring and discuss with your healthcare provider.",
    Elevated: "Measurement is above the typical range; discuss with your healthcare provider for further evaluation.",
    Low: "Measurement is below the typical range; discuss with your healthcare provider for further evaluation.",
  }

  return templates[statusText] || feature.interpretation
}

export function generateProviderInterpretation(feature, colorClass, features, hrBpm) {
  if (colorClass === "grey") {
    return "No clinical diagnosis recorded — assessment pending"
  }
  if (feature.name === "Structural Sound" || feature.name === "Structural Sound Summary") {
    const structuralLabel = structuralSoundResultLabel(feature, colorClass)
    const note = getAutoProviderNote(
      feature.name,
      colorClass,
      feature.observed,
      feature.displayReferenceRange || feature.normalRange,
      hrBpm
    )
    if (structuralLabel === "Unable to assess") {
      return (
        note ||
        "Structural-sound guide evidence was limited; do not interpret this as a detected repeatable structural-sound pattern."
      )
    }
  }
  const statusText = interpretationStatusLabel(feature, colorClass)

  if (isRhythmRegularityFeature(feature)) {
    const pattern =
      rhythmRegularityPatternLabel(feature, colorClass) ||
      (colorClass === "green"
        ? "Expected PCG timing variability"
        : colorClass === "red"
        ? "High PCG timing variability"
        : "Moderate PCG timing variability")
    const displayPattern = pcgTimingDisplayLabel(pattern)
    return `${displayPattern}. ${rhythmRegularityProviderMethodFromFeature(
      feature
    )} PCG timing proxy; not ECG HRV or ECG rhythm diagnosis.`
  }

  // Check if feature has specific provider interpretation text
  if (statusText === "Normal" && feature.providerNormal) {
    let text = feature.providerNormal
    if (feature.actionNormal) {
      text += "\n\nRecommended Action: " + feature.actionNormal
    }
    return text
  } else if (statusText === "Borderline" && feature.providerBorderline) {
    let text = feature.providerBorderline
    if (feature.actionBorderline) {
      text += "\n\nRecommended Action: " + feature.actionBorderline
    }
    return text
  } else if ((statusText === "Elevated" || statusText === "Low") && feature.providerAbnormal) {
    let text = feature.providerAbnormal
    if (feature.actionAbnormal) {
      text += "\n\nRecommended Action: " + feature.actionAbnormal
    }
    return text
  }

  // Fallback to generic templates
  const templates = {
    Normal: "Within expected physiologic range; no immediate concerns.",
    Borderline: "Borderline result; consider in clinical context and potential follow-up.",
    Elevated:
      "Result above expected physiologic range; correlate with clinical presentation and consider further evaluation.",
    Low: "Result below expected physiologic range; correlate with clinical presentation and consider further evaluation.",
  }

  return templates[statusText] || feature.providerInterpretation
}
